<script lang="ts">
  import { apiClient, uploadFormData } from '@/utils/api';

  interface PresentationItem {
    id: string;
    slug: string;
    title: string;
    description?: string;
    category?: string;
    presentation_type: 'html' | 'pdf' | 'external';
    external_url?: string;
    is_published: boolean;
    show_on_profile: boolean;
    position: number;
    presented_at?: string;
    thumbnail_url?: string;
    has_pdf: boolean;
    has_html_content: boolean;
    created_at: string;
  }

  let {
    presentations: initialPresentations = [],
    categories = [],
  }: {
    presentations: PresentationItem[];
    categories: string[];
  } = $props();

  // svelte-ignore state_referenced_locally
  const initialPresentationItems = initialPresentations;
  let presentations = $state<PresentationItem[]>(initialPresentationItems);
  let showModal = $state(false);
  let editingPt = $state<PresentationItem | null>(null);
  let saving = $state(false);
  let deleteConfirm = $state<string | null>(null);

  // Form state
  let form = $state({
    title: '',
    slug: '',
    description: '',
    category: '',
    presentation_type: 'pdf' as 'html' | 'pdf' | 'external',
    external_url: '',
    // 허용제 — 새 자료는 본인이 켜기 전까지 어디에도 안 보인다(2026-09-17).
    is_published: false,
    show_on_profile: false,
    presented_at: '',
  });
  let pdfFile = $state<File | null>(null);
  let htmlFile = $state<File | null>(null);
  let htmlAssetFiles = $state<File[]>([]);
  let thumbnailFile = $state<File | null>(null);
  let thumbnailPreview = $state<string | null>(null);
  let formErrors = $state<Record<string, string[]>>({});

  function openCreate() {
    editingPt = null;
    form = {
      title: '', slug: '', description: '', category: '',
      presentation_type: 'pdf', external_url: '',
      is_published: false, show_on_profile: false, presented_at: '',
    };
    pdfFile = null;
    htmlFile = null;
    htmlAssetFiles = [];
    thumbnailFile = null;
    thumbnailPreview = null;
    formErrors = {};
    showModal = true;
  }

  function openEdit(pt: PresentationItem) {
    editingPt = pt;
    form = {
      title: pt.title,
      slug: pt.slug,
      description: pt.description || '',
      category: pt.category || '',
      presentation_type: pt.presentation_type,
      external_url: pt.external_url || '',
      is_published: pt.is_published,
      show_on_profile: pt.show_on_profile,
      presented_at: pt.presented_at ? pt.presented_at.split('T')[0] : '',
    };
    pdfFile = null;
    htmlFile = null;
    htmlAssetFiles = [];
    thumbnailFile = null;
    thumbnailPreview = pt.thumbnail_url || null;
    formErrors = {};
    showModal = true;
  }

  function autoSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[가-힣]+/g, (m) => m.split('').join('-'))
      .replace(/[^a-z0-9\-]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60);
  }

  function handleTitleInput() {
    if (!editingPt) {
      form.slug = autoSlug(form.title);
    }
  }

  function handleThumbnailSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    thumbnailFile = file;
    thumbnailPreview = URL.createObjectURL(file);
  }

  function handlePdfSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    pdfFile = file;
  }

  function handleHtmlSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    htmlFile = file;
  }

  function handleHtmlAssetsSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files) return;
    htmlAssetFiles = Array.from(input.files);
  }

  async function handleSave() {
    saving = true;
    formErrors = {};

    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('slug', form.slug);
      formData.append('description', form.description);
      formData.append('category', form.category);
      formData.append('presentation_type', form.presentation_type);
      formData.append('external_url', form.external_url);
      formData.append('is_published', String(form.is_published));
      formData.append('show_on_profile', String(form.show_on_profile));
      if (form.presented_at) formData.append('presented_at', form.presented_at);
      if (pdfFile) formData.append('pdf_file', pdfFile);
      if (htmlFile) formData.append('html_file', htmlFile);
      htmlAssetFiles.forEach(f => formData.append('html_assets[]', f));
      if (thumbnailFile) formData.append('thumbnail', thumbnailFile);

      let response;
      if (editingPt) {
        response = await uploadFormData<{ presentation: PresentationItem }>(
          `/api/v1/presentations/${editingPt.id}`,
          formData,
          'PATCH',
        );
      } else {
        response = await uploadFormData<{ presentation: PresentationItem }>(
          '/api/v1/presentations',
          formData,
          'POST',
        );
      }

      if (response.success && response.data?.presentation) {
        const pt = response.data.presentation;
        if (editingPt) {
          presentations = presentations.map((p) => (p.id === pt.id ? pt : p));
        } else {
          presentations = [...presentations, pt];
        }
        showModal = false;
      } else {
        formErrors = (response as any).errors || {};
      }
    } catch {
      formErrors = { base: ['저장 중 오류가 발생했습니다.'] };
    } finally {
      saving = false;
    }
  }

  async function handleDelete(id: string) {
    const response = await apiClient.delete(`/api/v1/presentations/${id}`, { skipCaseConversion: true });
    if (response.success) {
      presentations = presentations.filter((p) => p.id !== id);
    }
    deleteConfirm = null;
  }

  async function toggleField(pt: PresentationItem, field: 'is_published' | 'show_on_profile') {
    const newVal = !pt[field];
    const response = await apiClient.patch<{ presentation: PresentationItem }>(
      `/api/v1/presentations/${pt.id}`,
      { [field]: newVal },
      { skipCaseConversion: true }
    );
    if (response.success && response.data?.presentation) {
      presentations = presentations.map((p) => (p.id === pt.id ? response.data!.presentation : p));
    }
  }

  const typeLabel: Record<string, string> = { html: 'HTML', pdf: 'PDF', external: '외부 링크' };
</script>

<div class="max-w-4xl mx-auto px-4 py-8 sm:px-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-8">
      <div>
        <h1 class="text-2xl font-bold text-content" style="font-family: 'Outfit', sans-serif;">
          발표자료 관리
        </h1>
        <p class="mt-1 text-sm text-content-tertiary">
          발표자료를 업로드하고 프로필에 표시할 항목을 선택하세요
        </p>
      </div>
      <button
        onclick={openCreate}
        class="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        새 발표자료
      </button>
    </div>

    <!-- List -->
    {#if presentations.length === 0}
      <div class="text-center py-16 bg-surface border border-default">
        <svg class="mx-auto w-12 h-12 text-gray-300 dark:text-dark-text-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="square" stroke-width="1.5" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
        </svg>
        <p class="mt-4 text-content-tertiary">아직 등록된 발표자료가 없습니다</p>
        <button onclick={openCreate} class="mt-4 text-sm text-blue-600 hover:underline">
          첫 발표자료 추가하기
        </button>
      </div>
    {:else}
      <div class="space-y-3">
        {#each presentations as pt (pt.id)}
          <div class="flex gap-4 bg-surface border border-default p-4 hover:border-gray-300 transition-colors">
            <!-- Thumbnail -->
            <div class="w-32 h-20 bg-surface-tertiary shrink-0 overflow-hidden">
              {#if pt.thumbnail_url}
                <img src={pt.thumbnail_url} alt={pt.title} class="w-full h-full object-cover" />
              {:else}
                <div class="w-full h-full flex items-center justify-center">
                  <svg class="w-8 h-8 text-gray-300 dark:text-dark-text-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="square" stroke-width="1.5" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
                  </svg>
                </div>
              {/if}
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <h3 class="font-medium text-content truncate">{pt.title}</h3>
                  <div class="flex items-center gap-2 mt-1">
                    <span class="text-xs px-1.5 py-0.5 bg-surface-tertiary text-content-secondary">
                      {typeLabel[pt.presentation_type] || pt.presentation_type}
                    </span>
                    {#if pt.category}
                      <span class="text-xs text-gray-400 dark:text-dark-text-disabled">{pt.category}</span>
                    {/if}
                    <span class="text-xs text-gray-400 dark:text-dark-text-disabled">/pt/{pt.slug}</span>
                  </div>
                </div>
              </div>

              <!-- Toggles -->
              <div class="flex items-center gap-4 mt-2">
                <label class="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pt.is_published}
                    onchange={() => toggleField(pt, 'is_published')}
                    class="accent-blue-600"
                  />
                  <span class="text-content-secondary">공개</span>
                </label>
                <label class="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pt.show_on_profile}
                    onchange={() => toggleField(pt, 'show_on_profile')}
                    class="accent-blue-600"
                  />
                  <span class="text-content-secondary">프로필 표시</span>
                </label>
                <div class="ml-auto flex gap-2">
                  <button
                    onclick={() => openEdit(pt)}
                    class="text-xs text-gray-500 hover:text-blue-600 transition-colors"
                  >편집</button>
                  {#if deleteConfirm === pt.id}
                    <button onclick={() => handleDelete(pt.id)} class="text-xs text-red-600 font-medium">확인</button>
                    <button onclick={() => (deleteConfirm = null)} class="text-xs text-gray-400">취소</button>
                  {:else}
                    <button onclick={() => (deleteConfirm = pt.id)} class="text-xs text-gray-400 hover:text-red-500 transition-colors">삭제</button>
                  {/if}
                </div>
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}

    <!-- Public PT page link -->
    <div class="mt-6 text-center">
      <a href="/pt" target="_blank" class="text-sm text-gray-400 hover:text-blue-600 transition-colors">
        공개 발표자료 페이지 보기 →
      </a>
    </div>
  </div>

  <!-- Modal -->
  {#if showModal}
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div class="bg-surface w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4 border border-default shadow-xl">
        <div class="sticky top-0 bg-surface border-b border-default px-6 py-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold text-content">
            {editingPt ? '발표자료 편집' : '새 발표자료'}
          </h2>
          <button onclick={() => (showModal = false)} class="text-gray-400 hover:text-gray-600" aria-label="모달 닫기">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="p-6 space-y-4">
          {#if formErrors.base}
            <p class="text-sm text-red-600">{formErrors.base.join(', ')}</p>
          {/if}

          <!-- Title -->
          <div>
            <label for="presentation-title" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">제목 *</label>
            <input
              id="presentation-title"
              type="text"
              bind:value={form.title}
              oninput={handleTitleInput}
              class="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-tertiary text-sm focus:outline-none focus:border-blue-500"
              placeholder="발표 제목"
            />
            {#if formErrors.title}<p class="text-xs text-red-500 mt-1">{formErrors.title.join(', ')}</p>{/if}
          </div>

          <!-- Slug -->
          <div>
            <label for="presentation-slug" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">슬러그 *</label>
            <div class="flex items-center gap-1 text-sm text-gray-400">
              <span>/pt/</span>
              <input
                id="presentation-slug"
                type="text"
                bind:value={form.slug}
                class="flex-1 px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-tertiary text-sm focus:outline-none focus:border-blue-500"
                placeholder="my-presentation"
              />
            </div>
            {#if formErrors.slug}<p class="text-xs text-red-500 mt-1">{formErrors.slug.join(', ')}</p>{/if}
          </div>

          <!-- Description -->
          <div>
            <label for="presentation-description" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">설명</label>
            <textarea
              id="presentation-description"
              bind:value={form.description}
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-tertiary text-sm focus:outline-none focus:border-blue-500 resize-none"
              placeholder="간단한 설명"
            ></textarea>
          </div>

          <!-- Category + Date -->
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label for="presentation-category" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">카테고리</label>
              <select
                id="presentation-category"
                bind:value={form.category}
                class="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-tertiary text-sm focus:outline-none focus:border-blue-500"
              >
                <option value="">선택안함</option>
                {#each categories as cat}
                  <option value={cat}>{cat}</option>
                {/each}
              </select>
            </div>
            <div>
              <label for="presentation-date" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">발표일</label>
              <input
                id="presentation-date"
                type="date"
                bind:value={form.presented_at}
                class="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-tertiary text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>

          <!-- Type -->
          <div>
            <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-2">타입 *</div>
            <div class="flex gap-4">
              {#each [['pdf', 'PDF 업로드'], ['external', '외부 링크'], ['html', 'HTML (고급)']] as [val, label]}
                <label class="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" name="pt_type" value={val} bind:group={form.presentation_type} class="accent-blue-600" />
                  <span class="text-gray-700 dark:text-dark-text-medium">{label}</span>
                </label>
              {/each}
            </div>
          </div>

          <!-- Type-specific fields -->
          {#if form.presentation_type === 'pdf'}
            <div>
              <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">PDF 파일</div>
              <label class="block border-2 border-dashed border-gray-300 dark:border-dark-border hover:border-blue-400 transition-colors cursor-pointer p-6 text-center">
                <input type="file" accept=".pdf" onchange={handlePdfSelect} class="hidden" />
                {#if pdfFile}
                  <p class="text-sm text-blue-600">{pdfFile.name}</p>
                  <p class="text-xs text-gray-400 mt-1">{(pdfFile.size / 1024 / 1024).toFixed(1)}MB</p>
                {:else if editingPt?.has_pdf}
                  <p class="text-sm text-gray-500">기존 PDF 업로드됨 — 새 파일로 교체하려면 클릭</p>
                {:else}
                  <svg class="mx-auto w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
                  </svg>
                  <p class="mt-2 text-sm text-gray-500">클릭하여 PDF 선택 (최대 50MB)</p>
                {/if}
              </label>
              {#if formErrors.pdf_file}<p class="text-xs text-red-500 mt-1">{formErrors.pdf_file.join(', ')}</p>{/if}
            </div>
          {:else if form.presentation_type === 'external'}
            <div>
              <label for="presentation-external-url" class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">외부 URL *</label>
              <input
                id="presentation-external-url"
                type="url"
                bind:value={form.external_url}
                class="w-full px-3 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-tertiary text-sm focus:outline-none focus:border-blue-500"
                placeholder="https://docs.google.com/presentation/..."
              />
              {#if formErrors.external_url}<p class="text-xs text-red-500 mt-1">{formErrors.external_url.join(', ')}</p>{/if}
            </div>
          {:else if form.presentation_type === 'html'}
            <div class="space-y-3">
              <div>
                <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">HTML 파일 *</div>
                <label class="block border-2 border-dashed border-gray-300 dark:border-dark-border hover:border-blue-400 transition-colors cursor-pointer p-6 text-center">
                  <input type="file" accept=".html,.htm" onchange={handleHtmlSelect} class="hidden" />
                  {#if htmlFile}
                    <p class="text-sm text-blue-600">{htmlFile.name}</p>
                    <p class="text-xs text-gray-400 mt-1">{(htmlFile.size / 1024).toFixed(1)}KB</p>
                  {:else if editingPt?.has_html_content}
                    <p class="text-sm text-gray-500">HTML 업로드됨 -- 새 파일로 교체하려면 클릭</p>
                  {:else}
                    <svg class="mx-auto w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
                    </svg>
                    <p class="mt-2 text-sm text-gray-500">클릭하여 HTML 파일 선택</p>
                  {/if}
                </label>
                {#if formErrors.html_file}<p class="text-xs text-red-500 mt-1">{formErrors.html_file.join(', ')}</p>{/if}
                {#if formErrors.file_path}<p class="text-xs text-red-500 mt-1">{formErrors.file_path.join(', ')}</p>{/if}
              </div>
              <div>
                <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">이미지/에셋 파일</div>
                <label class="block border-2 border-dashed border-gray-300 dark:border-dark-border hover:border-blue-400 transition-colors cursor-pointer p-4 text-center">
                  <input
                    type="file"
                    accept="image/*,.css,.js,.mjs,.json,.svg,.woff,.woff2,.ttf,.otf"
                    multiple
                    onchange={handleHtmlAssetsSelect}
                    class="hidden"
                  />
                  {#if htmlAssetFiles.length > 0}
                    <p class="text-sm text-blue-600">{htmlAssetFiles.length}개 파일 선택됨</p>
                    <p class="text-xs text-gray-400 mt-1">
                      {htmlAssetFiles.map(f => f.name).join(', ')}
                    </p>
                  {:else}
                    <p class="text-sm text-gray-500">클릭하여 이미지/CSS/JS/폰트 파일 선택 (여러 파일 가능)</p>
                  {/if}
                </label>
              </div>
            </div>
          {/if}

          <!-- Thumbnail -->
          <div>
            <div class="block text-sm font-medium text-gray-700 dark:text-dark-text-medium mb-1">썸네일</div>
            <div class="flex items-start gap-3">
              <div class="w-32 h-20 bg-surface-tertiary border border-default overflow-hidden shrink-0">
                {#if thumbnailPreview}
                  <img src={thumbnailPreview} alt="썸네일 미리보기" class="w-full h-full object-cover" />
                {:else}
                  <div class="w-full h-full flex items-center justify-center text-gray-300 text-xs">미리보기</div>
                {/if}
              </div>
              <label class="flex-1 border border-dashed border-gray-300 dark:border-dark-border hover:border-blue-400 transition-colors cursor-pointer p-3 text-center text-sm text-gray-500">
                <input type="file" accept="image/jpeg,image/png,image/webp" onchange={handleThumbnailSelect} class="hidden" />
                이미지 선택 (최대 5MB)
              </label>
            </div>
            {#if formErrors.thumbnail}<p class="text-xs text-red-500 mt-1">{formErrors.thumbnail.join(', ')}</p>{/if}
          </div>

          <!-- Toggles -->
          <div class="flex gap-6">
            <label class="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" bind:checked={form.is_published} class="accent-blue-600" />
              <span class="text-gray-700 dark:text-dark-text-medium">공개</span>
            </label>
            <label class="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" bind:checked={form.show_on_profile} class="accent-blue-600" />
              <span class="text-gray-700 dark:text-dark-text-medium">프로필에 표시</span>
            </label>
          </div>
        </div>

        <!-- Footer -->
        <div class="sticky bottom-0 bg-surface border-t border-default px-6 py-4 flex justify-end gap-3">
          <button
            onclick={() => (showModal = false)}
            class="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >취소</button>
          <button
            onclick={handleSave}
            disabled={saving || !form.title || !form.slug}
            class="px-4 py-2 bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? '저장 중...' : (editingPt ? '수정' : '추가')}
          </button>
        </div>
      </div>
    </div>
  {/if}
